import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography } from '@mui/material'
import Markdown from 'markdown-to-jsx'
import { useMarkdownOverrides } from './useMarkdownOverrides.tsx'

interface MarkdownRendererProps {
  readonly content: string
  readonly fallbackContent?: string
}

export const MarkdownRenderer: FC<MarkdownRendererProps> = ({
  content,
  fallbackContent = 'No content available',
}) => {
  const markdownOverrides = useMarkdownOverrides()

  if (!content?.trim()) {
    return (
      <Typography variant="body2" color="text.secondary" sx={fallbackTextSx}>
        {fallbackContent}
      </Typography>
    )
  }

  const processedContent = stripLeadingThematicBreak(content)

  try {
    return (
      <Box sx={contentWrapperSx}>
        <Markdown options={markdownOverrides}>{processedContent}</Markdown>
      </Box>
    )
  } catch {
    return (
      <Typography variant="body2" color="error" sx={errorTextSx}>
        Failed to render content
      </Typography>
    )
  }
}

function stripLeadingThematicBreak(content: string): string {
  return content.replace(/^---\s*\n/, '')
}

const fallbackTextSx: SxProps<Theme> = {
  fontStyle: 'italic',
}

const errorTextSx: SxProps<Theme> = {
  fontStyle: 'italic',
}

const contentWrapperSx: SxProps<Theme> = {
  '& > *:first-of-type': { mt: 0 },
  '& > *:last-child': { mb: 0 },
}
